import ActivityAppState from '../apps/activities/state'
import initOfficeAddIn from '../apps/agave/microsoft/office_init'
import Skeleton from '../models/skeleton'
// import constructActivity from '../util/construct_activity'

/**
 * Tracks application state
 *
 * @event change:theme Theme change
 *  @property {String} name Theme name
 *
 * @event session:start User logged in and model hydrated
 *  @property {Model} user User model
 *
 * @event session:logout User logged out
 * @event session:anonymous No user on startup
 *
 */

function pageOrVisibilityChanged (model) {
  // the console logs below will help debug
  // console.log(`>>> visible (${model.get('slideVisible')}) page (${model.get('page')})`)
  if (model.get('page') === 'activities') {
    model.appStates.activities.changePage('activities')
  }
  if (model.get('page') === 'presenter' && model.get('slideVisible')) {
    // console.log('>>> activating!')
    model.appStates.activities.get('activity').activate()
  }
}

export default class State extends Backbone.Model {
  get defaults () {
    return {
      role: 'agave',
      page: null,
      settings: null,
      user: null,
      participant: null,
      session: null,
      // general app ready state
      ready: false,
      slideVisible: false
    }
  }

  get defaultPage () {
    return 'activities'
  }

  get loggedIn () {
    return !!this.get('user').get('id')
  }

  initialize (attrs = {}, options = {}) {
    // Track states for apps
    this.appStates = {
      activities: new ActivityAppState({
        user: attrs.user,
        mode: attrs.mode,
        role: 'agave',
        checkable: false,
        showTrash: false
      })
    }

    this.listenTo(this.appStates.activities, 'change:activity', (state, activity) => {
      if (activity instanceof Skeleton) return

      if (!this.officeModel) {
        console.log('[ERROR] office model not yet setup')
        return
      }

      if (!activity) {
        // clear office url
        this.officeModel.save({})
        return
      }

      // strip out type/perma, make url, save to office object
      console.log(`ready to save: ${activity.attributes.type}/${activity.attributes.permalink}`)

      // why are we checking for both the type and permalink?
      // There is a weird condition in desktop powerpoint that
      // when opening, we don't open the proper activity, and it
      // saves the undefined/undefined object, resetting to the activites page
      // The best guess is either the activity is not fully hydrated
      // and therefore the type and permalink aren't set, but we
      // have an activity and are setting them to null  or the
      // office model's presenter id is flubbed based on this line during a repro:
      // [Error] Unhandled Promise Rejection: TypeError: undefined is not an object (evaluating 'activity.presenter.id')
      //
      // for some reason ensuring that both the type and permalink exist, it fixes the issue
      //
      activity.attributes.type && activity.attributes.permalink && this.officeModel.save({
        url: `${activity.attributes.type}/${activity.attributes.permalink}`
      })
    })

    this.on('change:page', pageOrVisibilityChanged.bind(this))
    this.on('change:slideVisible', pageOrVisibilityChanged.bind(this))

    // Session is fully loaded with user
    this.on('session:start', () => {
      // Session started. Set app to ready state
      if (this.get('participant').get('must_consent')) {
        // check whether we should show the terms page first
        this.set({
          ready: true,
          page: 'terms_agreement'
        })
      } else {
        this.set({ ready: true })
      }
    })

    // Automatically go to the login page if we're initially logged out
    this.on('session:logout session:anonymous', () => {
      // When the page changes, we're assuming the app is ready
      // Session started. Set app to ready state
      this.set({ ready: true })
    })

    // Nicety
    this.get('session').on('session:stopped', () => {
      this.trigger('session:logout')
    })

    this.get('session').on('session:started', () => {
      // Session start could have an anonymous user
      if (this.get('user').id) {
        // if the user is a participant, convert them to presenter
        if (!this.get('user').get('account_id')) {
          Backbone.ajax({
            method: 'POST',
            url: '/api/account'
          }).done((json) => {
            if (json) {
              this.get('user').set({ account: json })
              // must fetch the groups here since fetching them as a participant fails
              this.get('user').groups.fetch()
            }
          })
        // the session doesn't always fetch account data, so do that here
        } else if (this.get('user').get('account') && Object.keys(this.get('user').get('account')).length === 0) {
          this.get('session').start({
            includes: ['account']
          })
        }

        this.trigger('session:start', this.get('user'))
      } else {
        this.trigger('session:anonymous')
      }
    })

    // If agave initialization race conditions become frequent,
    // move this into entry/agave.js
    console.log('initializing office')
    initOfficeAddIn(this)
    console.log('office initilization complete')
  }

  changePage (page, options = {}) {
    this.set({ page }, options)
  }

  inspect () {
    const result = super.toJSON()

    // Deal with state model attributes whose values are complex types
    result.participant = this.get('participant')?.toJSON()
    result.settings = this.get('settings')?.toJSON()
    result.user = this.get('user')?.toJSON().user

    // session only has references to the participant and user, which we otherwise have anyway
    delete result.session

    // Deal with state object properties tacked on outside of the underlying Backbone.Model
    result.appStates = {}
    Object.entries(this.appStates).forEach(([key, value]) => {
      result.appStates[key] = value.inspect()
    })

    return result
  }
}
